import React, { useCallback, useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { formatPlayCount } from '../lib/utils';

// ── Design helpers ────────────────────────────────────────────────────────────

const card = { boxShadow: '0px 2px 8px 0px rgba(0,0,0,0.04)' };

const STATUS_STYLES = {
  pending:   'bg-amber-50 text-amber-700 border border-amber-200',
  approved:  'bg-blue-50 text-blue-700 border border-blue-200',
  monitored: 'bg-[#ecf8fb] text-[#2a798c] border border-[#3fb5d2]/30',
  verified:  'bg-purple-50 text-purple-700 border border-purple-200',
  rejected:  'bg-red-50 text-[#c62828] border border-red-200',
  closed:    'bg-gray-100 text-[#646464] border border-gray-200',
};

const STATUS_LABELS = {
  pending: 'Pending', approved: 'Approved', monitored: 'Monitored',
  verified: 'Verified', rejected: 'Rejected', closed: 'Closed',
};

const StatCell = ({ label, value, border }) => (
  <div className={`flex flex-col gap-1 py-3 px-3 ${border ? 'border-r border-[#e5e5e5]' : ''}`}>
    <span className="text-[11px] text-[#8a8a8a] leading-4">{label}</span>
    <span className="text-[15px] font-semibold text-[#3b3b3b] leading-5">{value}</span>
  </div>
);

const StatSection = ({ title, metrics }) => (
  <div className="flex-1 min-w-0 border border-[#e5e5e5] rounded-xl overflow-hidden">
    <div className="px-3 pt-3 pb-2 border-b border-[#e5e5e5]">
      <p className="text-[12px] font-medium text-[#646464]">{title}</p>
    </div>
    <div className="grid grid-cols-2">
      <StatCell label="Total Views"    value={formatPlayCount(metrics.views)}    border />
      <StatCell label="Total Likes"    value={formatPlayCount(metrics.likes)} />
      <div className="col-span-2 border-t border-[#e5e5e5] grid grid-cols-2">
        <StatCell label="Total Comments" value={formatPlayCount(metrics.comments)} border />
        <StatCell label="Total Shares"   value={formatPlayCount(metrics.shares)} />
      </div>
    </div>
  </div>
);

const SectionCard = ({ children, className = '' }) => (
  <div className={`bg-white rounded-2xl ${className}`} style={card}>
    {children}
  </div>
);

// ── Top Performing helpers ────────────────────────────────────────────────────

const getYouTubeId = (url) => {
  if (!url || typeof url !== 'string') return null;
  const trimmed = url.trim();
  const shortLink = trimmed.match(/youtu\.be\/([^/?&#\s]+)/);
  if (shortLink) return shortLink[1];

  try {
    const u = new URL(trimmed);
    const host = u.hostname.toLowerCase();
    if (!host.includes('youtube.com') && !host.includes('youtu.be')) return null;

    const v = u.searchParams.get('v');
    if (v) return v;

    const path = u.pathname || '';
    const shorts = path.match(/^\/shorts\/([^/?]+)/);
    if (shorts) return shorts[1];

    const embed = path.match(/^\/embed\/([^/?]+)/);
    if (embed) return embed[1];

    const live = path.match(/^\/live\/([^/?]+)/);
    if (live) return live[1];
  } catch {
    /* fall through */
  }

  const legacy = trimmed.match(/[?&]v=([^&?/#\s]+)/);
  return legacy ? legacy[1] : null;
};

const getTikTokId = (url) => {
  if (!url) return null;
  const match = url.match(/tiktok\.com\/@[^/]+\/video\/(\d+)/);
  return match ? match[1] : null;
};

const getInstagramShortcode = (url) => {
  if (!url) return null;
  const match = url.match(/instagram\.com\/(?:p|reels?|tv)\/([A-Za-z0-9_-]+)/i);
  return match ? match[1] : null;
};

const PlatformIcon = ({ platform }) => {
  if (platform === 'Instagram') return (
    <svg viewBox="0 0 24 24" fill="none" className="w-4 h-4" stroke="currentColor" strokeWidth="1.75">
      <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
      <circle cx="12" cy="12" r="4"/>
      <circle cx="17.5" cy="6.5" r="0.8" fill="currentColor" stroke="none"/>
    </svg>
  );
  if (platform === 'Tiktok') return (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
      <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.26 6.26 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.18 8.18 0 004.78 1.52V6.75a4.85 4.85 0 01-1.01-.06z"/>
    </svg>
  );
  return (
    <svg viewBox="0 0 24 24" fill="none" className="w-4 h-4" stroke="currentColor" strokeWidth="1.75">
      <path d="M22.54 6.42a2.78 2.78 0 00-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46A2.78 2.78 0 001.46 6.42 29 29 0 001 12a29 29 0 00.46 5.58 2.78 2.78 0 001.95 1.96C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 001.95-1.96A29 29 0 0023 12a29 29 0 00-.46-5.58z"/>
      <polygon points="9.75,15.02 15.5,12 9.75,8.98 9.75,15.02" fill="white"/>
    </svg>
  );
};

const TopPerformingCard = ({ proposal, rank }) => {
  const platform = proposal.social_media_platform || '';
  const ytId = getYouTubeId(proposal.social_media_link);
  const ttId = getTikTokId(proposal.social_media_link);
  const igCode = getInstagramShortcode(proposal.social_media_link);
  const [playing, setPlaying] = useState(false);
  const username = proposal.user_profiles?.full_name || proposal.user_profiles?.email || 'Unknown';
  const contentName = platform ? platform + ' Post' : 'Content';

  const ytThumbnail = ytId ? `https://img.youtube.com/vi/${ytId}/hqdefault.jpg` : null;

  const renderMedia = () => {
    // YouTube — thumbnail shown immediately, click to play
    if (ytId) {
      if (playing) {
        return (
          <iframe
            src={`https://www.youtube.com/embed/${ytId}?autoplay=1`}
            title="YouTube"
            allow="autoplay; encrypted-media"
            allowFullScreen
            className="w-full h-full"
          />
        );
      }
      return (
        <div className="relative w-full h-full cursor-pointer group" onClick={() => setPlaying(true)}>
          <img src={ytThumbnail} alt="thumbnail" className="w-full h-full object-cover" />
          <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors">
            <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center shadow-md">
              <svg viewBox="0 0 24 24" fill="#3fb5d2" className="w-6 h-6 ml-0.5">
                <polygon points="5,3 19,12 5,21"/>
              </svg>
            </div>
          </div>
        </div>
      );
    }

    // TikTok — iframe loaded immediately, thumbnail shown by TikTok's own embed UI
    if (platform === 'Tiktok' && ttId) {
      return (
        <iframe
          src={`https://www.tiktok.com/embed/v2/${ttId}`}
          title="TikTok"
          allow="autoplay; encrypted-media"
          allowFullScreen
          className="w-full h-full"
          sandbox="allow-scripts allow-same-origin allow-popups"
        />
      );
    }

    // Instagram — iframe loaded immediately, thumbnail shown by Instagram's own embed UI
    if (platform === 'Instagram' && igCode) {
      return (
        <iframe
          src={`https://www.instagram.com/p/${igCode}/embed/`}
          title="Instagram"
          allowFullScreen
          className="w-full h-full"
          sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
        />
      );
    }

    return (
      <div className="w-full h-full bg-gray-100 flex items-center justify-center">
        <span className="text-[#8a8a8a] text-[12px]">No preview</span>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-2xl overflow-hidden flex flex-col" style={card}>
      {/* Media area — 9:16 portrait ratio so videos aren't cut off */}
      <div className="relative w-full" style={{ paddingBottom: '177.78%' }}>
        <div className="absolute inset-0">
          {renderMedia()}

          {/* Rank badge */}
          <div className="absolute top-2.5 left-2.5 w-7 h-7 rounded-full bg-[#3fb5d2] flex items-center justify-center shadow-sm pointer-events-none">
            <span className="text-white text-[11px] font-bold leading-none">#{rank}</span>
          </div>

          {/* Platform icon */}
          <div className="absolute top-2.5 right-2.5 w-7 h-7 rounded-full bg-white border border-[#e5e5e5] flex items-center justify-center shadow-sm text-[#646464] pointer-events-none">
            <PlatformIcon platform={platform} />
          </div>
        </div>
      </div>

      {/* Info */}
      <div className="p-3 flex-1 flex flex-col gap-0.5">
        <p className="text-[13px] font-semibold text-[#3b3b3b] truncate">{contentName}</p>
        <p className="text-[12px] text-[#8a8a8a] truncate">@{username}</p>
        <div className="flex items-center gap-3 mt-1.5">
          <span className="text-[11px] text-[#646464]">
            <span className="font-semibold text-[#3b3b3b]">{formatPlayCount(proposal.like_count || 0)}</span> Likes
          </span>
          <span className="text-[11px] text-[#646464]">
            <span className="font-semibold text-[#3b3b3b]">{formatPlayCount(proposal.comment_count || 0)}</span> Comments
          </span>
        </div>
      </div>
    </div>
  );
};

const BtnPrimary = ({ children, onClick, disabled, className = '', type = 'button' }) => (
  <button
    type={type}
    onClick={onClick}
    disabled={disabled}
    className={`inline-flex items-center gap-2 bg-[#3fb5d2] hover:bg-[#2a798c] text-white text-[13px] font-semibold px-4 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
  >
    {children}
  </button>
);

const BtnOutline = ({ children, onClick, disabled, className = '', type = 'button' }) => (
  <button
    type={type}
    onClick={onClick}
    disabled={disabled}
    className={`inline-flex items-center gap-2 border border-[#e5e5e5] text-[#3b3b3b] hover:border-[#3fb5d2] hover:text-[#3fb5d2] text-[13px] font-medium px-4 py-2 rounded-lg transition-colors disabled:opacity-50 ${className}`}
  >
    {children}
  </button>
);

const BtnDanger = ({ children, onClick, disabled, className = '', type = 'button' }) => (
  <button
    type={type}
    onClick={onClick}
    disabled={disabled}
    className={`inline-flex items-center gap-2 border border-red-200 text-[#c62828] hover:bg-red-50 text-[13px] font-medium px-4 py-2 rounded-lg transition-colors disabled:opacity-50 ${className}`}
  >
    {children}
  </button>
);

// ── Main component ────────────────────────────────────────────────────────────

const CampaignDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { profile, user } = useAuth();
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isRegistered, setIsRegistered] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [proposals, setProposals] = useState([]);
  const [loadingProposals, setLoadingProposals] = useState(false);
  const [syncingStats, setSyncingStats] = useState(false);
  const [selectedProposals, setSelectedProposals] = useState(new Set());
  const [verifying, setVerifying] = useState(false);
  const [showAddLinkForm, setShowAddLinkForm] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [loadingParticipants, setLoadingParticipants] = useState(false);
  const [adminOperatorForm, setAdminOperatorForm] = useState({
    social_media_link: '', social_media_platform: '', hunter_ids: [],
  });
  const [submittingLink, setSubmittingLink] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);

  const loadParticipants = useCallback(async () => {
    try {
      setLoadingParticipants(true);
      const data = await api.get(`/api/campaigns/${id}/participants`);
      setParticipants(data || []);
    } catch { setParticipants([]); }
    finally { setLoadingParticipants(false); }
  }, [id]);

  useEffect(() => {
    loadCampaign(); checkRegistration(); loadProposals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, profile?.role]);

  useEffect(() => {
    if (showAddLinkForm && (profile?.role === 'admin_operator' || profile?.role === 'admin') && id) {
      loadParticipants();
    }
  }, [showAddLinkForm, profile?.role, id, loadParticipants]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        if ((profile?.role === 'hunter' || profile?.role === 'basic') && isRegistered) loadProposals();
        else if (profile?.role === 'admin' || profile?.role === 'campaign_manager') loadProposals();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.role, isRegistered]);

  useEffect(() => {
    if (!loadingProposals && proposals.length > 0) {
      const pendingIds = new Set(
        proposals.filter(p => p.status === 'pending' && p.social_media_link && !p.display_status).map(p => p.id)
      );
      setSelectedProposals(prev => new Set(Array.from(prev).filter(id => pendingIds.has(id))));
    }
  }, [proposals, loadingProposals]);

  useEffect(() => {
    if (proposals.length > 0 && !loadingProposals) {
      const t1 = setTimeout(() => window.tiktokEmbed?.process?.(), 1200);
      const t2 = setTimeout(() => window.tiktokEmbed?.process?.(), 2000);
      return () => { clearTimeout(t1); clearTimeout(t2); };
    }
  }, [proposals, loadingProposals]);

  const loadCampaign = async () => {
    try {
      setLoading(true);
      const data = await api.get(`/api/campaigns/${id}`);
      setCampaign(data);
    } catch (error) {
      alert('Error loading campaign: ' + (error.message || 'Unknown error'));
      navigate('/campaigns');
    } finally { setLoading(false); }
  };

  const checkRegistration = async () => {
    if (profile?.role !== 'hunter' && profile?.role !== 'basic') return;
    try {
      const campaigns = await api.get('/api/campaigns');
      setIsRegistered(campaigns.find(c => c.id === id)?.is_registered || false);
    } catch { /* ignore */ }
  };

  const handleRegister = async () => {
    setRegistering(true);
    try {
      await api.post(`/api/campaigns/${id}/register`);
      setIsRegistered(true);
      alert('Berhasil mendaftar ke campaign!');
      loadCampaign();
    } catch (error) { alert(error.message || 'Gagal mendaftar ke campaign'); }
    finally { setRegistering(false); }
  };

  const handleStopCampaign = async () => {
    if (!window.confirm('Apakah Anda yakin ingin menghentikan campaign ini?')) return;
    try {
      await api.patch(`/api/campaigns/${id}/stop`, {});
      loadCampaign(); alert('Campaign berhasil dihentikan');
    } catch (error) { alert('Error stopping campaign: ' + error.message); }
  };

  const handleDeleteCampaign = async () => {
    if (!window.confirm('Apakah Anda yakin ingin menghapus campaign ini secara permanen? Semua proposal, pendaftaran, dan data terkait akan ikut terhapus.')) return;
    setDeleting(true);
    try {
      await api.delete(`/api/campaigns/${id}`);
      alert('Campaign berhasil dihapus'); navigate('/campaigns');
    } catch (error) { alert('Gagal menghapus campaign: ' + (error.response?.data?.error || error.message)); }
    finally { setDeleting(false); }
  };

  const handleExportToSheets = async () => {
    try {
      setExporting(true);
      const result = await api.post(`/api/campaigns/${id}/export-to-sheets`, {});
      setCampaign(prev => ({ ...prev, google_sheet_url: result.url }));
      window.open(result.url, '_blank');
    } catch (error) {
      alert('Gagal export: ' + (error.response?.data?.error || error.message));
    } finally { setExporting(false); }
  };

  const loadProposals = async () => {
    try {
      setLoadingProposals(true);
      const data = await api.get(`/api/campaigns/${id}/proposals`);
      setProposals(data || []);
      setTimeout(() => window.tiktokEmbed?.process?.(), 1000);
      setTimeout(() => window.tiktokEmbed?.process?.(), 2000);
    } catch { setProposals([]); }
    finally { setLoadingProposals(false); }
  };

  const handleToggleSelect = (proposalId) => {
    const next = new Set(selectedProposals);
    next.has(proposalId) ? next.delete(proposalId) : next.add(proposalId);
    setSelectedProposals(next);
  };

  const handleMassVerify = async () => {
    if (!selectedProposals.size) return alert('Pilih minimal satu proposal');
    if (!window.confirm(`Verifikasi ${selectedProposals.size} proposal sekaligus?`)) return;
    try {
      setVerifying(true);
      const result = await api.post('/api/proposals/mass-verify', { proposalIds: Array.from(selectedProposals) });
      alert(`Berhasil: ${result.success || 0}. Gagal: ${result.failed || 0}`);
      setSelectedProposals(new Set());
      setTimeout(() => { loadProposals(); setTimeout(() => window.tiktokEmbed?.process?.(), 1500); }, 200);
    } catch (error) { alert('Error: ' + (error.response?.data?.error || error.message)); }
    finally { setVerifying(false); }
  };

  const handleSyncStatistics = async () => {
    if (!window.confirm('Sync statistik semua proposal yang sedang dimonitor?')) return;
    try {
      setSyncingStats(true);
      const result = await api.post(`/api/campaigns/${id}/sync-statistics`, {}, { timeout: 600000 }); // 10 min — sync analytics bisa lama
      alert(result.message || `Sync berhasil untuk ${result.proposals_count || 0} proposal`);
      loadProposals();
    } catch (error) { alert('Error syncing: ' + (error.response?.data?.error || error.message)); }
    finally { setSyncingStats(false); }
  };

  const handleAdminOperatorSubmitLink = async (e) => {
    e.preventDefault();
    if (!adminOperatorForm.hunter_ids.length) return alert('Pilih minimal 1 hunter');
    const links = (adminOperatorForm.social_media_link || '').split(/\s+/).map(s => s.trim()).filter(Boolean);
    if (!links.length || !adminOperatorForm.social_media_platform) return alert('Link dan platform wajib diisi');
    try {
      setSubmittingLink(true);
      const payload = { social_media_platform: adminOperatorForm.social_media_platform, hunter_ids: adminOperatorForm.hunter_ids };
      if (links.length === 1) payload.social_media_link = links[0];
      else payload.social_media_links = links;
      const res = await api.post(`/api/campaigns/${id}/admin-operator-links`, payload);
      const data = res.data;
      const count = data?.count ?? (data?.proposals?.length) ?? (data?.id ? 1 : 0);
      const errors = data?.errors;
      if (errors?.length) alert(`${count} link ditambahkan. Gagal: ${errors.length}`);
      else alert(`${count === 1 ? 'Link' : count + ' link'} berhasil ditambahkan (auto-verified)`);
      setShowAddLinkForm(false);
      setAdminOperatorForm({ social_media_link: '', social_media_platform: '', hunter_ids: [] });
      loadProposals();
    } catch (error) { alert('Error: ' + (error.response?.data?.error || error.message)); }
    finally { setSubmittingLink(false); }
  };

  const handleHunterToggle = (userId) => {
    setAdminOperatorForm(prev => ({
      ...prev,
      hunter_ids: prev.hunter_ids.includes(userId)
        ? prev.hunter_ids.filter(i => i !== userId)
        : [...prev.hunter_ids, userId],
    }));
  };

  const getStatusStyle = (status, displayStatus) => STATUS_STYLES[displayStatus || status] || STATUS_STYLES.closed;
  const getStatusLabel = (status, displayStatus) => STATUS_LABELS[displayStatus || status] || (displayStatus || status);

  const calcMetrics = (list) => {
    const t = { views: 0, likes: 0, comments: 0, shares: 0 };
    list.forEach(p => {
      if (p.play_count != null)    t.views    += p.play_count    || 0;
      if (p.like_count != null)    t.likes    += p.like_count    || 0;
      if (p.comment_count != null) t.comments += p.comment_count || 0;
      if (p.share_count != null)   t.shares   += p.share_count   || 0;
    });
    return t;
  };

  const totalMetrics      = calcMetrics(proposals);
  const dailyTodoMetrics  = calcMetrics(proposals.filter(p => p.submitted_by_admin_operator));
  const bountyMetrics     = calcMetrics(proposals.filter(p => !p.submitted_by_admin_operator));

  const topByPlatform = (platform) =>
    [...proposals]
      .filter(p => p.social_media_link && p.social_media_platform === platform)
      .sort((a, b) => (b.play_count || 0) - (a.play_count || 0))
      .slice(0, 5);

  const topTikTok   = topByPlatform('Tiktok');
  const topInstagram = topByPlatform('Instagram');
  const topYouTube  = topByPlatform('Youtube');

  const canManage = profile?.role === 'admin' || profile?.role === 'campaign_manager';
  const canEdit   = canManage && (profile?.role === 'admin' || campaign?.created_by === user?.id);

  const fmtDate = (iso, opts = {}) =>
    new Date(iso).toLocaleString('id-ID', { timeZone: 'Asia/Jakarta', ...opts });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#3fb5d2] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!campaign) return null;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-4 min-w-0">

      {/* ── Campaign info card ── */}
      <SectionCard className="p-5">
        {/* Campaign header */}
        <div className="flex items-start gap-4">
          {campaign.image_url && (
            <img
              src={campaign.image_url}
              alt={campaign.name}
              className="w-[100px] h-[100px] rounded-xl object-cover flex-shrink-0"
            />
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <h1 className="text-[20px] font-semibold text-[#3b3b3b] leading-7 truncate">
                  {campaign.name}
                </h1>
                <p className="text-[13px] text-[#646464] mt-1 leading-5 line-clamp-2">
                  {campaign.description}
                </p>
                <p className="text-[11px] text-[#8a8a8a] mt-2">
                  Created at {fmtDate(campaign.created_at, { day: 'numeric', month: 'long', year: 'numeric' })} by {campaign.user_profiles?.full_name || 'Unknown'}
                </p>
                {campaign.participant_quota != null && (
                  <p className="text-[12px] text-[#8a8a8a] mt-1">
                    Links: {campaign.link_count ?? campaign.participant_count ?? 0} / {campaign.participant_quota}
                    {campaign.is_quota_full && <span className="ml-2 text-[#c62828] font-medium">(Penuh)</span>}
                  </p>
                )}
              </div>
              {/* Status badge */}
              <span className={`flex-shrink-0 inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-medium ${
                campaign.status === 'stopped' ? 'bg-gray-100 text-[#646464]' : 'bg-[#ecf8fb] text-[#2a798c]'
              }`}>
                {campaign.status === 'stopped' ? 'Closed' : 'Active'}
              </span>
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2 mt-4 pt-4 border-t border-[#e5e5e5] flex-wrap">
          {canEdit && (
            <Link
              to={`/campaigns/${id}/edit`}
              className="inline-flex items-center gap-2 bg-[#3fb5d2] hover:bg-[#2a798c] text-white text-[13px] font-semibold px-4 py-2 rounded-lg transition-colors"
            >
              Edit Campaign
            </Link>
          )}
          {canManage && campaign.status === 'active' && (
            <BtnOutline onClick={handleStopCampaign}>Close Campaign</BtnOutline>
          )}
          {profile?.role === 'admin' && (
            <BtnDanger onClick={handleDeleteCampaign} disabled={deleting}>
              {deleting ? 'Menghapus...' : 'Hapus Campaign'}
            </BtnDanger>
          )}
          {profile?.role === 'admin' && (
            <div className="flex items-center gap-2">
              <button
                onClick={handleExportToSheets}
                disabled={exporting}
                className="inline-flex items-center gap-2 px-4 py-2 text-[13px] font-semibold bg-[#0f9d58] hover:bg-[#0b7a45] text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {exporting ? (
                  <>
                    <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <svg viewBox="0 0 24 24" fill="none" className="w-4 h-4" stroke="currentColor" strokeWidth="2">
                      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
                      <polyline points="14 2 14 8 20 8"/>
                      <line x1="16" y1="13" x2="8" y2="13"/>
                      <line x1="16" y1="17" x2="8" y2="17"/>
                      <polyline points="10 9 9 9 8 9"/>
                    </svg>
                    {campaign.google_sheet_url ? 'Update Google Sheets' : 'Export ke Google Sheets'}
                  </>
                )}
              </button>
              {campaign.google_sheet_url && (
                <a
                  href={campaign.google_sheet_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-[13px] font-medium text-[#0f9d58] border border-[#0f9d58] hover:bg-[#f0faf5] rounded-lg transition-colors"
                >
                  <svg viewBox="0 0 24 24" fill="none" className="w-3.5 h-3.5" stroke="currentColor" strokeWidth="2">
                    <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/>
                    <polyline points="15 3 21 3 21 9"/>
                    <line x1="10" y1="14" x2="21" y2="3"/>
                  </svg>
                  Buka Sheet
                </a>
              )}
            </div>
          )}
        </div>
      </SectionCard>

      {/* ── Summary stats ── */}
      {proposals.length > 0 && (
        <SectionCard className="p-5">
          <h2 className="text-[15px] font-semibold text-[#3b3b3b] mb-1">Summary Campaign</h2>
          <p className="text-[13px] text-[#8a8a8a] mb-4">Ringkasan statistik per kategori</p>
          <div className="flex gap-4 flex-col md:flex-row">
            <StatSection title="Gabungan (Admin Operator + Hunter)" metrics={totalMetrics} />
            <StatSection title="Daily ToDo Link (Admin Operator Saja)" metrics={dailyTodoMetrics} />
            <StatSection title="Bounty Campaign (Hunter Saja)" metrics={bountyMetrics} />
          </div>
        </SectionCard>
      )}

      {/* ── Top 5 Performing ── */}
      {[
        { label: 'Top 5 TikTok',    list: topTikTok },
        { label: 'Top 5 Instagram', list: topInstagram },
        { label: 'Top 5 YouTube',   list: topYouTube },
      ].filter(({ list }) => list.length > 0).map(({ label, list }) => (
        <SectionCard key={label} className="p-5">
          <h2 className="text-[15px] font-semibold text-[#3b3b3b] mb-1">{label}</h2>
          <p className="text-[12px] text-[#8a8a8a] mb-4">Konten dengan views terbanyak</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {list.map((proposal, index) => (
              <TopPerformingCard key={proposal.id} proposal={proposal} rank={index + 1} />
            ))}
          </div>
        </SectionCard>
      ))}

      {/* ── Campaign details fields ── */}
      {(campaign.product || campaign.usp || campaign.pillar || campaign.important_term || campaign.makro || campaign.notes) && (
        <SectionCard className="p-5">
          <h2 className="text-[15px] font-semibold text-[#3b3b3b] mb-4">Campaign Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { key: 'product', label: 'Product' },
              { key: 'usp', label: 'USP' },
              { key: 'pillar', label: 'Pillar' },
              { key: 'important_term', label: 'Important Term' },
              { key: 'makro', label: 'Makro' },
            ].filter(f => campaign[f.key]).map(({ key, label }) => (
              <div key={key}>
                <p className="text-[11px] font-medium text-[#8a8a8a] uppercase tracking-wide mb-1">{label}</p>
                <p className="text-[13px] text-[#3b3b3b] whitespace-pre-wrap">{campaign[key]}</p>
              </div>
            ))}
          </div>
          {campaign.notes && (
            <div className="mt-4 pt-4 border-t border-[#e5e5e5]">
              <p className="text-[11px] font-medium text-[#8a8a8a] uppercase tracking-wide mb-1">Notes</p>
              <p className="text-[13px] text-[#3b3b3b] whitespace-pre-wrap">{campaign.notes}</p>
            </div>
          )}
        </SectionCard>
      )}

      {/* ── Hunter: join / submit ── */}
      {(profile?.role === 'hunter' || profile?.role === 'basic') && (
        <SectionCard className="p-5">
          <h2 className="text-[15px] font-semibold text-[#3b3b3b] mb-4">Join Campaign</h2>
          {isRegistered ? (
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
              <span className="inline-flex items-center gap-2 text-[#2a798c] text-[13px] font-medium">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20,6 9,17 4,12"/>
                </svg>
                Sudah Terdaftar
              </span>
              {campaign?.is_quota_full ? (
                <span className="text-[13px] text-amber-600 font-medium">Kuota Link Penuh</span>
              ) : (
                <Link
                  to={`/proposals/create?campaign_id=${id}`}
                  className="bg-[#3fb5d2] hover:bg-[#2a798c] text-white text-[13px] font-semibold px-4 py-2 rounded-lg transition-colors"
                >
                  Submit Post Link
                </Link>
              )}
            </div>
          ) : (
            <BtnPrimary onClick={handleRegister} disabled={registering}>
              {registering ? 'Mendaftar...' : 'Join Campaign'}
            </BtnPrimary>
          )}
        </SectionCard>
      )}

      {/* ── Submitted links (admin / campaign_manager / admin_operator) ── */}
      {(profile?.role === 'admin' || profile?.role === 'campaign_manager' || profile?.role === 'admin_operator') && (
        <SectionCard>
          {/* Header */}
          <div className="flex items-start justify-between px-5 py-4 border-b border-[#e5e5e5] gap-3 flex-wrap">
            <div>
              <h2 className="text-[17px] font-bold text-[#3b3b3b]">Submitted Link</h2>
              <p className="text-[13px] text-[#8a8a8a] mt-0.5">{proposals.length} link ditemukan</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {selectedProposals.size > 0 && (
                <BtnPrimary onClick={handleMassVerify} disabled={verifying || campaign?.status === 'stopped'}>
                  {verifying ? 'Verifying...' : `Verify (${selectedProposals.size})`}
                </BtnPrimary>
              )}
              {profile?.role === 'admin' && (
                <button
                  onClick={handleSyncStatistics}
                  disabled={syncingStats || campaign?.status === 'stopped'}
                  className="inline-flex items-center gap-2 border border-[#3fb5d2] text-[#3fb5d2] hover:bg-[#ecf8fb] text-[13px] font-medium px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="1,4 1,10 7,10"/>
                    <path d="M3.51 15a9 9 0 1 0 .49-3.8"/>
                  </svg>
                  {syncingStats ? 'Syncing...' : 'Statistic Sync'}
                </button>
              )}
              {(profile?.role === 'admin_operator' || profile?.role === 'admin') && campaign?.status !== 'stopped' && (
                <BtnPrimary onClick={() => setShowAddLinkForm(!showAddLinkForm)}>
                  + Add Todo List Link
                </BtnPrimary>
              )}
            </div>
          </div>

          {/* Inline add link form */}
          {showAddLinkForm && (profile?.role === 'admin_operator' || profile?.role === 'admin') && (
            <div className="px-5 py-4 border-b border-[#e5e5e5] bg-gray-50/50">
              <p className="text-[13px] font-semibold text-[#3b3b3b] mb-1">Tambah Daily Todo Link</p>
              <p className="text-[12px] text-[#8a8a8a] mb-3">Link akan auto-verified, tanpa payout, tidak masuk perhitungan tier.</p>
              <form onSubmit={handleAdminOperatorSubmitLink} className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <label className="text-[13px] font-medium text-[#3b3b3b]">Platform *</label>
                    <select
                      required
                      className="w-full h-10 border border-[#e5e5e5] rounded-lg px-3 text-[13px] text-[#3b3b3b] focus:outline-none focus:border-[#3fb5d2] focus:ring-1 focus:ring-[#3fb5d2] bg-white"
                      value={adminOperatorForm.social_media_platform}
                      onChange={e => setAdminOperatorForm(p => ({ ...p, social_media_platform: e.target.value }))}
                    >
                      <option value="">Pilih Platform</option>
                      <option>Instagram</option>
                      <option>Tiktok</option>
                      <option>Youtube</option>
                    </select>
                  </div>
                  <div className="space-y-1">
                    <label className="text-[13px] font-medium text-[#3b3b3b]">Social Media Link * <span className="text-[#8a8a8a] font-normal">(banyak link dipisah spasi / baris baru)</span></label>
                    <textarea
                      rows={2}
                      required
                      className="w-full border border-[#e5e5e5] rounded-lg px-3 py-2 text-[13px] text-[#3b3b3b] placeholder-[#8a8a8a] focus:outline-none focus:border-[#3fb5d2] focus:ring-1 focus:ring-[#3fb5d2] resize-none"
                      placeholder={'https://...\nhttps://...'}
                      value={adminOperatorForm.social_media_link}
                      onChange={e => setAdminOperatorForm(p => ({ ...p, social_media_link: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="space-y-1">
                  <label className="text-[13px] font-medium text-[#3b3b3b]">Hunter yang Berpartisipasi * <span className="text-[#8a8a8a] font-normal">(bisa lebih dari 1)</span></label>
                  {loadingParticipants ? (
                    <p className="text-[13px] text-[#8a8a8a]">Memuat daftar hunter...</p>
                  ) : participants.length === 0 ? (
                    <p className="text-[13px] text-amber-600">Belum ada hunter di sistem</p>
                  ) : (
                    <div className="border border-[#e5e5e5] rounded-lg p-3 max-h-36 overflow-y-auto bg-white grid grid-cols-2 md:grid-cols-3 gap-2">
                      {participants.map(p => (
                        <label key={p.user_id} className="flex items-center gap-2 cursor-pointer text-[13px] text-[#3b3b3b]">
                          <input
                            type="checkbox"
                            checked={adminOperatorForm.hunter_ids.includes(p.user_id)}
                            onChange={() => handleHunterToggle(p.user_id)}
                            className="h-4 w-4 rounded border-[#e5e5e5] text-[#3fb5d2] focus:ring-[#3fb5d2]"
                          />
                          {p.full_name || p.email || p.user_id}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex gap-2 pt-1">
                  <BtnPrimary type="submit" disabled={submittingLink || !adminOperatorForm.hunter_ids.length}>
                    {submittingLink ? 'Menyimpan...' : 'Simpan Link'}
                  </BtnPrimary>
                  <BtnOutline
                    onClick={() => { setShowAddLinkForm(false); setAdminOperatorForm({ social_media_link: '', social_media_platform: '', hunter_ids: [] }); }}
                  >
                    Batal
                  </BtnOutline>
                </div>
              </form>
            </div>
          )}

          {/* Table */}
          {loadingProposals ? (
            <div className="flex items-center justify-center py-10">
              <div className="w-6 h-6 border-2 border-[#3fb5d2] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : proposals.length === 0 ? (
            <p className="text-center text-[#8a8a8a] text-[14px] py-10">Belum ada link yang di-submit.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#e5e5e5]">
                    {['No','Hunter','Platform','Post Link','Type','Views','Like','Comment','Share','Status','Last Sync','Created At',''].map((h, i) => (
                      <th key={i} className={`text-[11px] font-medium text-[#8a8a8a] uppercase tracking-wider px-4 py-3 whitespace-nowrap ${['Views','Like','Comment','Share'].includes(h) ? 'text-right' : 'text-left'}`}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {proposals.map((p, idx) => {
                    const link = p.submitted_social_media_link || p.social_media_link;
                    const hunterName = p.submitted_by_admin_operator && p.proposal_participants?.length > 0
                      ? p.proposal_participants.map(pp => pp.full_name || pp.email || '?').join(', ')
                      : p.user_profiles?.full_name || p.user_profiles?.email || 'Unknown';
                    const isVerifiable = p.status === 'pending' && p.social_media_link && !p.display_status;
                    return (
                      <tr key={p.id} className="border-b border-[#e5e5e5] last:border-0 hover:bg-gray-50/50 transition-colors">
                        <td className="px-4 py-3 text-[13px] text-[#8a8a8a]">
                          <div className="flex items-center gap-2">
                            {isVerifiable && (
                              <input
                                type="checkbox"
                                checked={selectedProposals.has(p.id)}
                                onChange={() => handleToggleSelect(p.id)}
                                className="h-3.5 w-3.5 rounded border-[#e5e5e5] text-[#3fb5d2] focus:ring-[#3fb5d2] flex-shrink-0"
                              />
                            )}
                            {idx + 1}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-[13px] text-[#3b3b3b] whitespace-nowrap max-w-[120px]">
                          <span className="block truncate">{hunterName}</span>
                        </td>
                        <td className="px-4 py-3">
                          {p.social_media_platform && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-medium bg-blue-50 text-blue-600 whitespace-nowrap">
                              {p.social_media_platform}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 max-w-[160px]">
                          {link ? (
                            <a href={link} target="_blank" rel="noopener noreferrer"
                              className="text-[#3fb5d2] hover:text-[#2a798c] text-[13px] block truncate transition-colors"
                              title={link}
                            >
                              {link}
                            </a>
                          ) : (
                            <span className="text-[#8a8a8a] text-[13px]">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-medium whitespace-nowrap ${
                            p.submitted_by_admin_operator ? 'bg-red-50 text-red-500' : 'bg-[#ecf8fb] text-[#2a798c]'
                          }`}>
                            {p.submitted_by_admin_operator ? 'Daily Todo' : 'Bounty'}
                          </span>
                        </td>
                        {[p.play_count, p.like_count, p.comment_count, p.share_count].map((val, i) => (
                          <td key={i} className="px-4 py-3 text-[13px] text-[#3b3b3b] text-right whitespace-nowrap">
                            {val != null ? formatPlayCount(val) : '—'}
                          </td>
                        ))}
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-medium whitespace-nowrap ${getStatusStyle(p.status, p.display_status)}`}>
                            {getStatusLabel(p.status, p.display_status)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-[12px] text-[#8a8a8a] whitespace-nowrap">
                          {p.last_sync_at ? fmtDate(p.last_sync_at, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                        </td>
                        <td className="px-4 py-3 text-[12px] text-[#8a8a8a] whitespace-nowrap">
                          {fmtDate(p.created_at, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Link to={`/proposals/${p.id}`} className="text-[#3fb5d2] hover:text-[#2a798c] text-[12px] font-medium transition-colors whitespace-nowrap">
                            Detail →
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </SectionCard>
      )}

      {/* ── Submitted links (hunter / basic) ── */}
      {(profile?.role === 'hunter' || profile?.role === 'basic') && isRegistered && (
        <SectionCard>
          <div className="px-5 py-4 border-b border-[#e5e5e5]">
            <h2 className="text-[17px] font-bold text-[#3b3b3b]">Submitted Link</h2>
            <p className="text-[13px] text-[#8a8a8a] mt-0.5">{proposals.length} link ditemukan</p>
          </div>
          {loadingProposals ? (
            <div className="flex items-center justify-center py-10">
              <div className="w-6 h-6 border-2 border-[#3fb5d2] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : proposals.length === 0 ? (
            <p className="text-center text-[#8a8a8a] text-[14px] py-10">Belum ada link yang di-submit.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#e5e5e5]">
                    {['No','Platform','Post Link','Views','Like','Comment','Share','Status','Last Sync','Created At',''].map((h, i) => (
                      <th key={i} className={`text-[11px] font-medium text-[#8a8a8a] uppercase tracking-wider px-4 py-3 whitespace-nowrap ${['Views','Like','Comment','Share'].includes(h) ? 'text-right' : 'text-left'}`}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {proposals.map((p, idx) => {
                    const link = p.submitted_social_media_link || p.social_media_link;
                    return (
                      <tr key={p.id} className="border-b border-[#e5e5e5] last:border-0 hover:bg-gray-50/50 transition-colors">
                        <td className="px-4 py-3 text-[13px] text-[#8a8a8a]">{idx + 1}</td>
                        <td className="px-4 py-3">
                          {p.social_media_platform && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-medium bg-blue-50 text-blue-600 whitespace-nowrap">
                              {p.social_media_platform}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 max-w-[200px]">
                          {link ? (
                            <a href={link} target="_blank" rel="noopener noreferrer"
                              className="text-[#3fb5d2] hover:text-[#2a798c] text-[13px] block truncate transition-colors"
                              title={link}
                            >
                              {link}
                            </a>
                          ) : (
                            <span className="text-[#8a8a8a] text-[13px]">—</span>
                          )}
                        </td>
                        {[p.play_count, p.like_count, p.comment_count, p.share_count].map((val, i) => (
                          <td key={i} className="px-4 py-3 text-[13px] text-[#3b3b3b] text-right whitespace-nowrap">
                            {val != null ? formatPlayCount(val) : '—'}
                          </td>
                        ))}
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-medium whitespace-nowrap ${getStatusStyle(p.status, p.display_status)}`}>
                            {getStatusLabel(p.status, p.display_status)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-[12px] text-[#8a8a8a] whitespace-nowrap">
                          {p.last_sync_at ? fmtDate(p.last_sync_at, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                        </td>
                        <td className="px-4 py-3 text-[12px] text-[#8a8a8a] whitespace-nowrap">
                          {fmtDate(p.created_at, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Link to={`/proposals/${p.id}`} className="text-[#3fb5d2] hover:text-[#2a798c] text-[12px] font-medium transition-colors whitespace-nowrap">
                            Detail →
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </SectionCard>
      )}
    </div>
  );
};

export default CampaignDetail;
